import { UserGroupIcon } from '@heroicons/react/24/outline';
import {
    communicationsInsightsWindowLabel,
    type CommunicationsInsightsGranularity,
    type CommunicationsInsightsUser
} from '../../../services/communicationsInsights';
import { CardErrorState } from './CardErrorState';
import { PERSON_COUNT_AVATAR_SIZE, PersonCountRow } from './PersonCountRow';

// Accounts Overview hub's "Communications per person" card - who logged the
// entries in the current window. Not clickable this round; the null-user
// "Unknown" bucket renders muted, distinct from named rows.
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full';
const SKELETON_ROW_COUNT = 4;

function SkeletonRow() {
    return (
        <div className="flex items-center gap-3 px-5 py-2.5">
            <div
                className="rounded-full bg-gray-100 animate-pulse shrink-0"
                style={{ width: PERSON_COUNT_AVATAR_SIZE, height: PERSON_COUNT_AVATAR_SIZE }}
            />
            <div className="flex-1 h-3 rounded bg-gray-100 animate-pulse" />
            <div className="w-6 h-3 rounded bg-gray-100 animate-pulse shrink-0" />
        </div>
    );
}

interface CommunicationsPerPersonCardProps {
    byUser: CommunicationsInsightsUser[];
    granularity: CommunicationsInsightsGranularity;
    isLoading: boolean;
    isError?: boolean;
    onRetry?: () => void;
    className?: string;
}

export function CommunicationsPerPersonCard({
    byUser,
    granularity,
    isLoading,
    isError = false,
    onRetry,
    className = ''
}: CommunicationsPerPersonCardProps) {
    return (
        <div className={`${CARD_CLASS} ${className}`.trim()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
                <h3 className="text-[15px] font-bold text-gray-800">Communications per person</h3>
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                    {communicationsInsightsWindowLabel(granularity)}
                </span>
            </div>
            {isLoading ? (
                <div className="py-1.5 flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                        <SkeletonRow key={i} />
                    ))}
                </div>
            ) : isError ? (
                <CardErrorState
                    message="Couldn't load communications per person"
                    onRetry={() => onRetry?.()}
                />
            ) : byUser.length === 0 ? (
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 px-6 py-6 text-center">
                    <UserGroupIcon className="h-8 w-8 text-gray-300" strokeWidth={1} />
                    <p className="text-sm text-gray-500">No communications in this period.</p>
                </div>
            ) : (
                /* Focusable: these rows are plain text with nothing tabbable
                   in them, so hiding the scrollbar left a keyboard user no
                   way to reach anyone past the fold. */
                <div
                    className="flex-1 min-h-0 overflow-y-auto scrollbar-hide py-1"
                    tabIndex={0}
                    role="list"
                    aria-label="Communications per person"
                >
                    {byUser.map((person) => (
                        <PersonCountRow
                            key={person.userId ?? 'unknown'}
                            name={person.fullname}
                            count={person.count}
                            muted={person.userId === null}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
